import React, { useEffect, useMemo, useState } from "react";
import { listAll, updateDocById } from "../../services/firestore";
import { Percent, Save, Tag, RotateCcw, AlertCircle, CheckCircle2, Filter, Package } from "lucide-react";

/**
 * AdminDiscounts — kütləvi endirim idarəetməsi.
 *
 * Hər məhsulda `discount_pct` sahəsi saxlanır (0..90).
 * `formatPrice` məntiqi və ProductCard discounted qiyməti tətbiq edir.
 *
 * Funksionallıq:
 *  - Bütün məhsullara birdəfəlik X% endirim tətbiq et
 *  - Müəyyən kateqoriyaya endirim tətbiq et
 *  - Yalnız "yeni gələnlər" və ya "ən çox satılanlar"a endirim
 *  - Bütün endirimlərı geri qaytar (sıfırla)
 */
export default function AdminDiscounts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [percent, setPercent] = useState(20);
  const [scope, setScope] = useState("all"); // "all" | "category" | "featured" | "new"
  const [categoryId, setCategoryId] = useState("");

  // UI state
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [message, setMessage] = useState(null); // {type: success|error, text}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState("apply"); // "apply" | "reset"

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([listAll("products"), listAll("categories")]);
        setProducts(p);
        setCategories(c);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Hesabla: scope əsasında hansı məhsullar təsirə düşür
  const targetProducts = useMemo(() => {
    if (scope === "all") return products;
    if (scope === "category" && categoryId) {
      return products.filter((p) => {
        if (p.category_id === categoryId) return true;
        return Array.isArray(p.category_ids) && p.category_ids.includes(categoryId);
      });
    }
    if (scope === "featured") return products.filter((p) => p.featured);
    if (scope === "new") return products.filter((p) => p.new_arrival);
    return [];
  }, [products, scope, categoryId]);

  const productsWithDiscount = useMemo(
    () => products.filter((p) => Number(p.discount_pct || 0) > 0),
    [products]
  );

  // Sample preview: orijinal qiymət + yeni qiymət
  const previewSamples = useMemo(() => targetProducts.slice(0, 3), [targetProducts]);

  const applyDiscount = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    setProgress({ done: 0, total: targetProducts.length });
    const pct = Math.max(0, Math.min(90, Number(percent) || 0));
    let done = 0;
    let errors = 0;
    for (const p of targetProducts) {
      try {
        await updateDocById("products", p.id, { discount_pct: pct });
        done += 1;
      } catch {
        errors += 1;
      }
      setProgress({ done: done + errors, total: targetProducts.length });
    }
    // Reload
    const fresh = await listAll("products");
    setProducts(fresh);
    setBusy(false);
    setConfirmOpen(false);
    setMessage({
      type: errors === 0 ? "success" : "error",
      text: errors === 0
        ? `${done} məhsula ${pct}% endirim tətbiq edildi.`
        : `${done} uğurlu, ${errors} xəta. Yenidən cəhd edin.`,
    });
    setTimeout(() => setMessage(null), 5000);
  };

  const resetAllDiscounts = async () => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    const targets = productsWithDiscount;
    setProgress({ done: 0, total: targets.length });
    let done = 0;
    let errors = 0;
    for (const p of targets) {
      try {
        await updateDocById("products", p.id, { discount_pct: 0 });
        done += 1;
      } catch {
        errors += 1;
      }
      setProgress({ done: done + errors, total: targets.length });
    }
    const fresh = await listAll("products");
    setProducts(fresh);
    setBusy(false);
    setConfirmOpen(false);
    setMessage({
      type: errors === 0 ? "success" : "error",
      text: errors === 0
        ? `${done} məhsulun endirimi silindi.`
        : `${done} uğurlu, ${errors} xəta.`,
    });
    setTimeout(() => setMessage(null), 5000);
  };

  const openConfirm = (action) => {
    setActionType(action);
    setConfirmOpen(true);
  };

  if (loading) {
    return (
      <div className="p-10" data-testid="admin-discounts">
        <div className="metadata text-[var(--text-muted)]">Yüklənir...</div>
      </div>
    );
  }

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-discounts">
      {/* Header */}
      <div className="mb-10">
        <div className="metadata text-[var(--accent)]">SATIŞ ALƏTLƏRİ</div>
        <h1 className="font-display text-5xl mt-2">Kütləvi Endirim</h1>
        <p className="text-[var(--text-2)] text-sm mt-3 max-w-2xl leading-relaxed">
          Bir kliklə bütün məhsullara və ya seçilmiş qrupa endirim tətbiq edin. Qiymət saytda avtomatik üstüxətli orijinal qiymət və yeni endirimli qiymət ilə göstərilir.
        </p>
      </div>

      {/* Status banner */}
      {message && (
        <div
          className={`mb-6 flex items-center gap-3 px-4 py-3 border ${
            message.type === "success"
              ? "border-[var(--success)] text-[var(--success)]"
              : "border-[var(--error)] text-[var(--error)]"
          }`}
          data-testid="discounts-status"
        >
          {message.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
        <Card icon={Package} label="ÜMUMİ MƏHSUL" value={products.length} />
        <Card icon={Tag} label="ENDİRİMDƏ" value={productsWithDiscount.length} accent />
        <Card icon={Filter} label="SEÇİLİB" value={targetProducts.length} />
        <Card icon={Percent} label="ORTA ENDİRİM" value={`${avgDiscount(productsWithDiscount)}%`} />
      </div>

      {/* Main form card */}
      <div className="border border-[var(--border)] bg-[var(--surface)]">
        <div className="px-6 py-4 border-b border-[var(--border)]">
          <div className="metadata">KONFİQURASİYA</div>
        </div>

        <div className="p-6 space-y-8">
          {/* 1. Percentage selection */}
          <div>
            <label className="metadata text-[var(--text-2)] mb-3 block">1. ENDİRİM FAİZİ</label>
            <div className="flex items-center gap-3 flex-wrap">
              {[10, 15, 20, 25, 30, 40, 50, 70].map((v) => (
                <button
                  key={v}
                  onClick={() => setPercent(v)}
                  className={`px-4 py-2 border transition-all font-display text-lg ${
                    Number(percent) === v
                      ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                      : "border-[var(--border)] hover:border-[var(--primary)]"
                  }`}
                  data-testid={`pct-preset-${v}`}
                >
                  {v}%
                </button>
              ))}
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  className="w-24 bg-transparent border border-[var(--border)] px-3 py-2 font-display text-lg text-center focus:border-[var(--primary)] outline-none"
                  data-testid="pct-custom"
                />
                <Percent size={16} className="text-[var(--text-muted)]" />
              </div>
            </div>
          </div>

          {/* 2. Scope */}
          <div>
            <label className="metadata text-[var(--text-2)] mb-3 block">2. TƏTBİQ ET</label>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { v: "all", label: "BÜTÜN MƏHSULLAR", desc: `${products.length} məhsul` },
                { v: "category", label: "KATEQORİYA", desc: "seçim aşağıda" },
                { v: "new", label: "YENİ GƏLƏNLƏR", desc: `${products.filter(p => p.new_arrival).length} məhsul` },
                { v: "featured", label: "SEÇİLMİŞ", desc: `${products.filter(p => p.featured).length} məhsul` },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setScope(opt.v)}
                  className={`text-left p-4 border transition-all ${
                    scope === opt.v
                      ? "bg-[var(--primary)] text-[var(--inverse)] border-[var(--primary)]"
                      : "border-[var(--border)] hover:border-[var(--primary)]"
                  }`}
                  data-testid={`scope-${opt.v}`}
                >
                  <div className="metadata">{opt.label}</div>
                  <div className={`text-xs mt-1 ${scope === opt.v ? "text-white/70" : "text-[var(--text-muted)]"}`}>
                    {opt.desc}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Category picker */}
          {scope === "category" && (
            <div>
              <label className="metadata text-[var(--text-2)] mb-2 block">KATEQORİYA SEÇ</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full md:w-96 bg-transparent border border-[var(--border)] px-3 py-2 focus:border-[var(--primary)] outline-none"
                data-testid="category-select"
              >
                <option value="">— seç —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Preview */}
          {targetProducts.length > 0 && (
            <div className="border-t border-[var(--border)] pt-6">
              <div className="metadata text-[var(--text-muted)] mb-4">
                ÖNBAXIŞ · {targetProducts.length} MƏHSULA TƏTBİQ EDİLƏCƏK
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {previewSamples.map((p) => {
                  const orig = Number(p.price_azn) || 0;
                  const newPrice = orig * (1 - Number(percent) / 100);
                  return (
                    <div key={p.id} className="border border-[var(--border)] p-4 bg-[var(--bg)]">
                      <div className="text-xs metadata text-[var(--text-muted)] truncate mb-2">{p.title}</div>
                      <div className="flex items-baseline gap-2">
                        <span className="line-through text-[var(--text-muted)] text-sm">₼{orig.toFixed(2)}</span>
                        <span className="font-display text-xl text-[var(--accent)]">₼{newPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
                {targetProducts.length > 3 && (
                  <div className="border border-dashed border-[var(--border)] p-4 flex items-center justify-center text-[var(--text-muted)] metadata">
                    + {targetProducts.length - 3} DAHA
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="border-t border-[var(--border)] pt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={() => openConfirm("apply")}
              disabled={busy || targetProducts.length === 0 || !percent}
              className="btn-primary"
              data-testid="apply-discount-btn"
            >
              <Tag size={14} /> {targetProducts.length} MƏHSULA TƏTBİQ ET
            </button>

            <button
              onClick={() => openConfirm("reset")}
              disabled={busy || productsWithDiscount.length === 0}
              className="btn-outline"
              data-testid="reset-discount-btn"
            >
              <RotateCcw size={14} /> BÜTÜN ENDİRİMLƏRİ SIFIRLA
            </button>

            {busy && progress.total > 0 && (
              <div className="ml-2 flex items-center gap-3">
                <div className="w-32 h-1.5 bg-[var(--border)] overflow-hidden">
                  <div
                    className="h-full bg-[var(--primary)] transition-all"
                    style={{ width: `${(progress.done / progress.total) * 100}%` }}
                  />
                </div>
                <span className="metadata text-[var(--text-muted)]">
                  {progress.done} / {progress.total}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Currently discounted list */}
      {productsWithDiscount.length > 0 && (
        <div className="mt-10">
          <div className="metadata text-[var(--text-muted)] mb-4">HAL-HAZIRDA ENDİRİMDƏ</div>
          <div className="border border-[var(--border)] divide-y divide-[var(--border-soft)]">
            {productsWithDiscount.slice(0, 10).map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3" data-testid={`disc-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {p.cover_url && <img src={p.cover_url} alt="" className="w-10 h-10 object-cover" />}
                  <div className="min-w-0">
                    <div className="text-sm truncate">{p.title}</div>
                    <div className="metadata text-[var(--text-muted)] text-[10px]">
                      ₼{Number(p.price_azn).toFixed(2)} → ₼{(Number(p.price_azn) * (1 - p.discount_pct / 100)).toFixed(2)}
                    </div>
                  </div>
                </div>
                <span className="pill-accent">-{p.discount_pct}%</span>
              </div>
            ))}
            {productsWithDiscount.length > 10 && (
              <div className="px-4 py-3 metadata text-[var(--text-muted)] text-center">
                + {productsWithDiscount.length - 10} daha
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => !busy && setConfirmOpen(false)}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
                {actionType === "apply" ? <Tag size={18} /> : <AlertCircle size={18} className="text-[var(--accent)]" />}
              </div>
              <div>
                <div className="metadata text-[var(--text-muted)]">{actionType === "apply" ? "TƏTBİQ" : "SIFIRLAMA"}</div>
                <h3 className="font-display text-2xl mt-1">Əminsiniz?</h3>
              </div>
            </div>
            <p className="text-[var(--text-2)] text-sm leading-relaxed mb-6">
              {actionType === "apply"
                ? `${targetProducts.length} məhsula ${percent}% endirim tətbiq ediləcək. Bu əməliyyat geri qaytarıla bilər.`
                : `${productsWithDiscount.length} məhsulun endirimi silinəcək və orijinal qiymətə qayıdacaq.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmOpen(false)} disabled={busy} className="btn-ghost" data-testid="confirm-cancel">
                LƏĞV ET
              </button>
              <button
                onClick={actionType === "apply" ? applyDiscount : resetAllDiscounts}
                disabled={busy}
                className="btn-primary"
                data-testid="confirm-action"
              >
                {busy ? "TƏTBİQ EDİLİR…" : (actionType === "apply" ? "TƏTBİQ ET" : "SIFIRLA")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
      <Icon size={16} className={accent ? "text-[var(--accent)]" : "text-[var(--text-muted)]"} />
      <div className="font-display text-3xl mt-2">{value}</div>
      <div className="metadata text-[10px] text-[var(--text-muted)] mt-1">{label}</div>
    </div>
  );
}

function avgDiscount(items) {
  if (!items.length) return 0;
  const sum = items.reduce((s, p) => s + Number(p.discount_pct || 0), 0);
  return Math.round(sum / items.length);
}
